/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel, { CampaignUpdateTypes } from 'cio/models/campaign';
import { task } from 'ember-concurrency';
import { action, set } from '@ember/object';
import TagModel from 'cio/models/tag';
import { inject as service } from '@ember/service';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { LAST_30_DAYS_RANGE_PRESET } from 'cio/utils/workflow-metrics';
import { Periods, Channel, Entities, PERIOD_KEYS } from 'cio/utils/metrics/common';
import { tracked } from '@glimmer/tracking';
import MetricsService from 'cio/services/metrics';
import { hash } from 'ember-concurrency';
import { JourneyMetrics } from 'cio/utils/journey-metrics';
import { Metric, summarizeMetrics } from 'cio/utils/metrics/models/metric';
import moment from 'moment-timezone';
import { Grouping } from 'cio/utils/metrics/resolution';
import { SortDirection } from 'cio/utils/sort';
import { isEmpty } from '@ember/utils';
import SettingsService from 'cio/services/settings';
import { ACTION_INFO } from 'cio/utils/action-info';
import { ActionType } from 'cio/models/action';
import AuthService from 'cio/services/auth';

interface CampaignOverviewPageArgs {
  campaign: CampaignModel;

  startTime: string;
  endTime: string;
  onTimeRangeChange: (start: string, end: string) => unknown;

  period: Periods;
  onPeriodChange: (period: Periods) => unknown;

  grouping: Grouping;
  onGroupingChange: (grouping: Grouping, trackEvent?: boolean) => unknown;

  channels: Channel[];
  onChannelsChange: (channels: Channel[]) => unknown;

  leaderboardTab: string;
  onLeaderboardTabChange: (tab: string) => unknown;

  leaderboardSortDirection: SortDirection;
  onLeaderboardSortChange: (sortDirection: SortDirection) => unknown;
}

export default class CampaignOverviewPage extends Component<CampaignOverviewPageArgs> {
  @service auth!: AuthService;
  @service metrics!: MetricsService;
  @service settings!: SettingsService;

  @tracked timeRange = LAST_30_DAYS_RANGE_PRESET.getRange();

  @tracked channelMetrics: Partial<Record<Channel, Metric>> = {};
  @tracked journeyMetric: JourneyMetrics | null = null;
  @tracked totalCampaignMetric: Metric | null = null;
  @tracked actionDeliveryMetrics: Record<string, Metric> = {};

  constructor(owner: unknown, args: CampaignOverviewPageArgs) {
    super(owner, args);

    if (!isEmpty(args.startTime) && !isEmpty(args.endTime)) {
      this.timeRange = {
        start: moment.tz(args.startTime, this.settings.environmentSettings.metricsTimezone),
        end: moment.tz(args.endTime, this.settings.environmentSettings.metricsTimezone),
      };
    }
  }

  get hasReadOnlyPermission() {
    if (this.args.campaign.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  fetchMetrics = task({ keepLatest: true }, async () => {
    let { period } = this.args;
    if (period === 'week') {
      period = Periods.CUSTOM;
    }
    let metrics = await hash({
      campaignChannelMetrics: this.metrics.fetchCampaignChannelDeliveryMetrics(
        this.args.campaign.id,
        period,
        this.timeRange,
      ),
      journeyMetric: this.metrics.fetchJourneyMetrics(this.args.campaign.id, Periods.DAYS, {
        start: moment.tz(
          this.timeRange.start.format('YYYY-MM-DD'),
          this.settings.environmentSettings.journeyMetricsTimezone,
        ),
        end: moment.tz(
          this.timeRange.end.format('YYYY-MM-DD'),
          this.settings.environmentSettings.journeyMetricsTimezone,
        ),
      }),
      actionDeliveryMetrics: this.metrics.fetchCampaignActionDeliveryMetrics(
        this.args.campaign.id,
        period,
        this.timeRange,
      ),
    });

    set(metrics.journeyMetric, 'grouping', this.args.grouping);
    Object.values(metrics.campaignChannelMetrics).forEach((m: Metric) => set(m, 'grouping', this.args.grouping));

    this.channelMetrics = metrics.campaignChannelMetrics as Record<Channel, Metric>;
    this.journeyMetric = metrics.journeyMetric;
    this.actionDeliveryMetrics = metrics.actionDeliveryMetrics;

    this.sumChannelMetrics();
  });

  @action
  private sumChannelMetrics() {
    let relevantChannelMetrics = Object.entries(this.channelMetrics).reduce<Metric[]>((acc, [channel, metric]) => {
      if (!this.args.channels.includes(channel as Channel) || metric == null) {
        return acc;
      }

      acc.push(metric);

      return acc;
    }, []);

    relevantChannelMetrics.forEach((metric: Metric) => {
      metric.canConvert = this.canActionConvert(metric.canConvert, metric.actionType as ActionType);
    });

    this.totalCampaignMetric = summarizeMetrics(this.args.campaign.id, Entities.CAMPAIGN, null, relevantChannelMetrics);
  }

  @action
  private canActionConvert(canConvert: boolean, actionType: ActionType): boolean {
    if (canConvert) {
      return true;
    }

    let actionInfo = ACTION_INFO[actionType];
    if (actionInfo != null && actionInfo.optionallyConverts === true) {
      let actionTypeString = actionType as string;
      return Object.keys(this.actionDeliveryMetrics).some(
        (m: string) =>
          this.actionDeliveryMetrics[m].actionType === actionTypeString && this.actionDeliveryMetrics[m].canConvert,
      );
    }

    return false;
  }

  saveCampaignDetails = task({ drop: true }, async (attr: keyof CampaignModel, value: any) => {
    this.args.campaign.set(attr, value);
    this.args.campaign.set('updateType', CampaignUpdateTypes.Main);

    return await this.args.campaign.save();
  });

  @action
  addTag(tag: TagModel) {
    return this.args.campaign.addTags([tag]);
  }

  @action
  removeTag(tag: TagModel) {
    return this.args.campaign.removeTag(tag);
  }

  @action
  onRangeSelect(range: DateRange, period: Periods) {
    this.timeRange = range;

    this.args.onPeriodChange(period);
    if (this.args.grouping !== PERIOD_KEYS[period]) {
      this.args.onGroupingChange(PERIOD_KEYS[period], false);
    }
    this.args.onTimeRangeChange(range.start.format('YYYY-MM-DD'), range.end.format('YYYY-MM-DD'));

    this.fetchMetrics.perform();
  }

  @action
  onChannelsSelect(channels: Channel[]) {
    this.args.onChannelsChange(channels);

    this.sumChannelMetrics();
  }

  @action
  onGroupingSelect(grouping: Grouping) {
    this.args.onGroupingChange(grouping);

    this.fetchMetrics.perform();
  }
}
